import { useEmojiMartData } from "@circle-react-shared/uikit/EmojiPickerV3/Pickers/EmojiMart/useEmojiMartData";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { useKeyboardHandler } from "@circle-react-uikit/TipTap/Extensions/CustomExtensions/hooks/useKeyboardHandler";
import { useFetchEmojis } from "@circle-react-uikit/TipTapBlockEditor/InlineEmojiExtension/hooks/useFetchEmojis";
import type { Editor, Range } from "@tiptap/react";
import { isEmpty } from "lodash";
import { forwardRef, useEffect, useState } from "react";

import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { EmojiItem } from "./EmojiItem";
import { EmojiItemCompass } from "./EmojiItemCompass";

export const EMOJI_LIST_ID = "emojiList";

interface EmojiPickerProps {
  editor: Editor;
  query: string;
  range: Range;
}

export const EmojiPicker = forwardRef(
  (
    { editor, range, query }: EmojiPickerProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { setPopupsVisible } = useInternalEditorState();
    const { isCircleV4 } = useCircleV4();

    useEmojiMartData();
    const { emojis } = useFetchEmojis(query);

    useEffect(() => setSelectedIndex(0), [emojis]);

    useEffect(() => {
      setPopupsVisible(!isEmpty(emojis));

      return () => setPopupsVisible(false);
    }, [emojis, setPopupsVisible]);

    const insertEmoji = (emoji: any) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent(emoji.skins[0].native)
        .insertContent(" ")
        .run();
    };

    useEffect(() => {
      document
        .getElementById(`emoji-${emojis[selectedIndex]?.id}`)
        ?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
    }, [selectedIndex, emojis]);

    useKeyboardHandler({
      selectedIndex,
      setSelectedIndex,
      ref,
      items: emojis,
      selectItem: (index: number) => {
        const emoji = emojis[index];
        insertEmoji(emoji);
      },
    });

    if (isEmpty(emojis)) {
      return (
        <div className="h-[50vh] min-w-[250px] max-w-[300px] bg-transparent" />
      );
    }

    if (isCircleV4) {
      return (
        <div
          ref={ref}
          className="border-primary bg-primary relative max-h-[50vh] min-w-[250px] max-w-[300px] overflow-auto rounded-md border p-1 text-sm shadow-xs"
          id={EMOJI_LIST_ID}
        >
          {emojis.map((emoji, index) => {
            const isSelected = index === selectedIndex;
            return (
              <EmojiItemCompass
                key={emoji.id}
                emoji={emoji}
                index={index}
                setSelectedIndex={setSelectedIndex}
                isSelected={isSelected}
                insertEmoji={insertEmoji}
              />
            );
          })}
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className="border-primary bg-primary relative max-h-[50vh] min-w-[250px] max-w-[300px] overflow-auto rounded-sm border py-1.5 text-sm shadow-md"
        id={EMOJI_LIST_ID}
      >
        {emojis.map((emoji, index) => {
          const isSelected = index === selectedIndex;
          return (
            <EmojiItem
              key={emoji.id}
              emoji={emoji}
              index={index}
              setSelectedIndex={setSelectedIndex}
              isSelected={isSelected}
              insertEmoji={insertEmoji}
            />
          );
        })}
      </div>
    );
  },
);

EmojiPicker.displayName = "EmojiPicker";
